import { useEffect } from "react";
import { useEditor, EditorContent, useEditorState } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Bold, Italic, Underline, Link2, List, ListOrdered, Unlink } from "lucide-react";

const Tool = ({ on, active, title, children, testid }) => (
  <button type="button" title={title} aria-label={title} onMouseDown={(e) => e.preventDefault()} onClick={on} data-testid={testid}
    className={`h-8 w-8 grid place-items-center rounded-md transition-colors ${active ? "bg-[#0B192C] text-white" : "text-slate-600 hover:bg-slate-200"}`}>
    {children}
  </button>
);

const setLink = (editor) => {
  const prev = editor.getAttributes("link").href || "https://";
  const url = window.prompt("Indirizzo del link (URL)", prev);
  if (url === null) return;
  if (!url.trim() || url.trim() === "https://") return editor.chain().focus().unsetLink().run();
  const href = /^(https?:\/\/|mailto:)/i.test(url.trim()) ? url.trim() : `https://${url.trim()}`;
  if (editor.state.selection.empty) {
    editor.chain().focus().insertContent(`<a href="${href}">${href}</a> `).run();
  } else {
    editor.chain().focus().extendMarkRange("link").setLink({ href }).run();
  }
};

const Toolbar = ({ editor, prefix }) => {
  const s = useEditorState({
    editor,
    selector: ({ editor: e }) => ({
      bold: e.isActive("bold"), italic: e.isActive("italic"), underline: e.isActive("underline"),
      link: e.isActive("link"), bullet: e.isActive("bulletList"), ordered: e.isActive("orderedList"),
    }),
  });
  const c = () => editor.chain().focus();
  return (
    <div className="flex flex-wrap gap-1 p-1.5 border-b bg-slate-50 rounded-t-md" data-testid={`${prefix}-toolbar`}>
      <Tool title="Grassetto" active={s.bold} on={() => c().toggleBold().run()} testid={`${prefix}-bold`}><Bold className="h-4 w-4" /></Tool>
      <Tool title="Corsivo" active={s.italic} on={() => c().toggleItalic().run()} testid={`${prefix}-italic`}><Italic className="h-4 w-4" /></Tool>
      <Tool title="Sottolineato" active={s.underline} on={() => c().toggleUnderline().run()} testid={`${prefix}-underline`}><Underline className="h-4 w-4" /></Tool>
      <span className="w-px bg-slate-200 mx-1" />
      <Tool title="Inserisci link" active={s.link} on={() => setLink(editor)} testid={`${prefix}-link`}><Link2 className="h-4 w-4" /></Tool>
      {s.link && <Tool title="Rimuovi link" on={() => c().unsetLink().run()} testid={`${prefix}-unlink`}><Unlink className="h-4 w-4" /></Tool>}
      <span className="w-px bg-slate-200 mx-1" />
      <Tool title="Elenco puntato" active={s.bullet} on={() => c().toggleBulletList().run()} testid={`${prefix}-bullet`}><List className="h-4 w-4" /></Tool>
      <Tool title="Elenco numerato" active={s.ordered} on={() => c().toggleOrderedList().run()} testid={`${prefix}-ordered`}><ListOrdered className="h-4 w-4" /></Tool>
    </div>
  );
};

export const RichEditor = ({ value, onChange, minHeight = 260, testid = "rich-editor" }) => {
  const editor = useEditor({
    extensions: [StarterKit.configure({ heading: false, codeBlock: false, code: false, horizontalRule: false,
      link: { openOnClick: false, autolink: true, defaultProtocol: "https" } })],
    content: value || "",
    editorProps: { attributes: { class: "rich-text focus:outline-none px-3 py-2", style: `min-height:${minHeight}px`, "data-testid": `${testid}-content` } },
    onUpdate: ({ editor: e }) => onChange(e.isEmpty ? "" : e.getHTML()),
  });
  useEffect(() => {
    if (editor && value !== editor.getHTML() && !(value === "" && editor.isEmpty)) editor.commands.setContent(value || "", { emitUpdate: false });
  }, [value, editor]);
  if (!editor) return null;
  return (
    <div className="border border-input rounded-md bg-white focus-within:ring-2 focus-within:ring-emerald-500/40" data-testid={testid}>
      <Toolbar editor={editor} prefix={testid} />
      <EditorContent editor={editor} />
    </div>
  );
};

export const RichText = ({ html, className = "", ...rest }) => (
  <div className={`rich-text ${className}`} dangerouslySetInnerHTML={{ __html: html || "" }} {...rest} />
);
